import { HttpClient, HttpEventType } from '@angular/common/http';
import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { OidcSecurityService, UserDataResult } from 'angular-auth-oidc-client';
import { flatMap, forkJoin, Observable, of, map, mergeMap } from 'rxjs';
import { DaoService } from '../../../core/dao/dao.service';
import { ToastrService } from 'ngx-toastr';
import { ITileItem, TileItem } from '../../../core/models/tile-item.model';
import { AudioProcessorTask, AudioProcessorTaskBlock, IAudioProcessorTask, IAudioProcessorTaskBlock } from '../../../core/models/audio-processor-task.model';
import { IAudioPlugin, IMenuItem } from '../../../core/models/plugin.model';
import { TaskModel } from '../task.model';
import { AsyncService } from '../../../core/async/async.service';
import { FormControl, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { faTrashCan, faCirclePlay, faArrowPointer } from '@fortawesome/free-solid-svg-icons';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { IUser } from '../../../core/models/acustica-user.model';
import { TaskCreatorNameComponent } from './task.creator.name/task.creator.name.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ConsumptionModes, ITaskSettings } from '../../../core/models/task-settings.model';
import { Socket } from 'ngx-socket-io';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/auth/auth.service';
import { ProviderNames } from '../../../core/constants/provider.names.enum';
import { TaskCreatorInfoComponent } from './task.creator.info/task.creator.info.component';

const Resumable = require('../../../../assets/libs/resumable/resumable.js');
const MAX_FILES_PER_TASK = environment.taskConfiguration.maxFilesPerTask;
const MAX_TASK_NAME_LENGTH = environment.taskConfiguration.maxTaskNameLength;
const CLOUD_UPLOAD = environment.taskConfiguration.cloudUpload;
const PROVIDER_NAME = environment.taskConfiguration.providerName;
const NUM_SAMPLES = 4096;


@Component({
  selector: 'app-task-creator',
  templateUrl: 'task.creator.component.html',
  styleUrls: ['task.creator.component.scss']
})
export class TaskCreatorComponent implements OnInit {
  @Input() plugins: IAudioPlugin[] = [];
  public extensions: any[] = [".aac", ".flac", ".ogg", ".wav", ".mp3"];
  public loading: boolean = false;
  data!: any;
  items: ITileItem[] = [];
  progress!: number;
  faTrashCan = faTrashCan;
  faCirclePlay = faCirclePlay;
  faArrowPointer = faArrowPointer;
  user!: IUser;
  taskCreatorNameModalRef!: MatDialogRef<TaskCreatorNameComponent>;
  taskCreatorInfoModalRef!: MatDialogRef<TaskCreatorInfoComponent>;
  taskSettings!: ITaskSettings;
  socket!: any;
  fileCounter: number = 0;

  public taskNameInput: FormControl = new FormControl('taskNameInput', {
    validators: Validators.required,
    updateOn: 'blur'
  });

  constructor(
    private dao: DaoService,
    private toastr: ToastrService,
    public taskModel: TaskModel,
    private async: AsyncService,
    private router: Router,
    private dialog: MatDialog,
    private oidc: OidcSecurityService,
    private loader: NgxUiLoaderService,
    private authService: AuthService) { }

  ngOnInit() {
    // this.authService.sync(false)
    //   .pipe(
    //     flatMap((user) => {
    //       this.user = user;
    //       return this.dao.GetTaskSettings();
    //     })
    //   )
    //   .subscribe((taskSettings) => {
    //     this.taskSettings = taskSettings;
    //   },
    //     () => { },
    //     () => { })
    this.authService.sync(false)
      .pipe(
        flatMap((user) => {
          this.user = user;
          return this.dao.GetTaskSettings();
        })
      )
      .subscribe((taskSettings) => {
        this.taskSettings = taskSettings;
      },
        () => { },
        () => { })
  }

  clearTask() {
    if (this.taskModel.task.taskBlocks.length > 0) {
      this.async.openDialog("Clear Task", "You will lose any progress done configuring your task. Proceed?",
        (response) => {
          if (response)
            this.taskModel.clearTask();
        })
    }
    else {
      this.taskModel.clearTask();
    }
  }

  onBlockAdded($event: any) {
    const target = $event.target as HTMLInputElement;
    const files = target.files as FileList;
    this.addItems(files);
  }

  onBlockDropped(files: FileList) {
    this.addItems(files);
  }

  onPluginAdded($event: any) {

  }

  addItems(files: FileList) {
    this.taskModel.statusMessage = "Analyzing data...";
    this.enableLoading();
    for (var i = 0; i < files.length; i++) {
      let file: File = files[i];
      let block = new AudioProcessorTaskBlock();
      let item = new TileItem();
      let extension = "." + file.name.split(".")[file.name.split(".").length - 1];
      item.file = file;
      block.tileItem = item;
      if (this.extensions.indexOf(extension) > -1) {
        if (!this.taskModel.task.name) {
          this.taskModel.task.name = files[0].name.slice(0, Math.min(files[0].name.length, MAX_TASK_NAME_LENGTH));
        }
        this.taskModel.task.taskBlocks.push(block)
        this.fileCounter++;
      }
    }
  }

  loadBlock(data: any, error: any) {
    const waveInstance = data.waveInstance;
    const block = data.block;
    const blockIndex = this.taskModel.task.taskBlocks.indexOf(block);
    this.taskModel.task.taskBlocks[blockIndex].tileItem.duration = waveInstance.getDuration();
    this.taskModel.task.taskBlocks[blockIndex].tileItem.bytes = this.formatBytes(block, 2);
    this.taskModel.task.taskBlocks[blockIndex].fileDuration = waveInstance.getDuration();
    this.taskModel.task.taskBlocks[blockIndex].sampleRate = waveInstance.backend.ac.sampleRate;
    this.updateComputingUnitsCount();
    if (this.user.computingUnitsExtra + this.user.computingUnits < this.taskModel.task.computingUnits) {
      this.taskModel.task.taskBlocks = this.taskModel.task.taskBlocks.slice(0, blockIndex + 1);
      this.async.openAlert("Warning", "You don't have enugh computing units to process any more files", () => { });
      this.taskModel.statusMessage = "";
      this.disableLoading();
      return;
    }
    if (--this.fileCounter === 0) {
      this.taskModel.statusMessage = "";
      this.disableLoading();
    }
  }

  deleteBlock($event: Event, block: IAudioProcessorTaskBlock) {
    const index = this.taskModel.task.taskBlocks.indexOf(block);
    this.taskModel.task.taskBlocks.splice(index, 1);
    this.updateComputingUnitsCount();

  }

  previewBlock(event: Event) {
    let eventData = event as any;
    if (eventData.file.type == "audio/wav") {
      this.loader.start();
      this.taskModel.statusMessage = "Processing Preview..."
      this.dao
        .previewBlock(eventData.block, eventData.file)
        .toPromise()
        .then((result: any) => {
          var blob = new Blob([result.body], {
            type: eventData.file.type
          });
          var link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = eventData.file.name;
          link.click();
          this.loader.stop();
          this.taskModel.statusMessage = ""
        }, (error) => {
          this.loader.stop();
          this.taskModel.statusMessage = ""
        });

    } else {
      this.async.openAlert("Warning", "Preview is available for WAV files only", () => { });
    }

  }

  updateComputingUnitsCount() {
    const task = this.taskModel.task;
    const plugin = task.plugin as IAudioPlugin;
    this.taskModel.task.computingUnits = 0;
    for (var i = 0; i < task.taskBlocks.length; i++) {
      let block = task.taskBlocks[i];
      if (this.taskSettings.consumptionMode === ConsumptionModes.FILE_SIZE) {
        this.taskModel.task.computingUnits += Math.ceil(plugin.computingUnitsMultiplier * block.tileItem.file.size);

      } else if (this.taskSettings.consumptionMode === ConsumptionModes.TIME) {
        this.taskModel.task.computingUnits += Math.ceil(plugin.computingUnitsMultiplier * block.tileItem.duration);
      }
    }
  }

  startProcessing() {
    this.taskCreatorNameModalRef = this.dialog.open(TaskCreatorNameComponent);
    this.taskCreatorNameModalRef.afterClosed().subscribe((result) => {
      if (result) {
        if (CLOUD_UPLOAD)
          this.processCloudUpload();
        else
          this.processServerUpload();
      }
    });
  }

  processCloudUpload() {
    let task = this.taskModel.task;
    this.taskModel.statusMessage = "Creating Task...";
    this.enableLoading();
    this.validate()
      .then(() => {
        task.progress = -1;
        task = AudioProcessorTask.toServer(task);
        this.dao.AddTask(task)
          .pipe(
            flatMap((data: any) => {
              for (var i = 0; i < data.taskBlocks.length; i++) {
                let block = data.taskBlocks[i];
                let tileItem = task.taskBlocks[i].tileItem;
                block.tileItem = tileItem;
              }

              return of(data);
            }),
            flatMap((data: any) => {
              this.taskModel.statusMessage = "Uploading to the Cloud...";
              let calls = [];
              for (var i = 0; i < data.taskBlocks.length; i++) {
                let block = data.taskBlocks[i];
                calls.push(this.dao.GetUploadUrl(block.tileItem.file.name, block.taskId, block.id));
              }
              task = data;
              return forkJoin(calls);
            }),
            flatMap((uploadUrlsResponses: any[]) => {
              let calls = [];
              for (var i = 0; i < uploadUrlsResponses.length; i++) {
                let block = task.taskBlocks[i];
                let urlResponse = uploadUrlsResponses[i];
                let sourceUrl = this.user.id + "/" + block.taskId + "/" + block.id + "/" + block.tileItem.file.name;

                block.numSamples = NUM_SAMPLES;
                block.sourceUrl = sourceUrl;
                block.fileDuration = block.tileItem.duration;
                block.fileSize = block.tileItem.file.size;
                block.originalFileName = this.sanitizeFileName(block.tileItem.file.name);
                calls.push(
                  (PROVIDER_NAME == ProviderNames.Backblaze ?
                    this.dao.UploadFileToUrlBackblaze(block.tileItem.file, urlResponse, this.user.id, block.taskId, block.id) :
                    this.dao.UploadFileToUrlAmazon(block.tileItem.file, urlResponse, this.user.id, block.taskId, block.id))
                    .pipe(map(
                      event => {
                        if (event.type === HttpEventType.UploadProgress)
                          block.tileItem.progress = Math.round(100 * event.loaded / event.total);
                      }
                    ))
                );
              }

              return forkJoin(calls);
            }),
            flatMap((uploadResults: any[]) => {
              let calls = [];
              for (var i = 0; i < task.taskBlocks.length; i++) {
                let block = task.taskBlocks[i]
                calls.push(
                  this.dao
                    .UpdateTaskBlock(block)
                );
              }
              return forkJoin(calls);
            }),
            flatMap(() => {
              this.taskModel.statusMessage = "Queueing Task...";
              return this.dao.QueueTask(task.id).toPromise();
            }))
          .subscribe(data => {
            this.taskModel.statusMessage = "";
            this.toastr.success("Processing started");
            this.taskModel.clearTask();
            this.router.navigate(["processing"]);
          }, err => {
            this.taskModel.statusMessage = "";
            this.disableLoading();
            this.toastr.error("An error occurred preparing your task!");
          });
      })
  }

  processServerUpload() {
    let task = this.taskModel.task;
    this.taskModel.statusMessage = "Creating Task...";
    this.validate()
      .then(() => {
        this.enableLoading();
        task.name = task.name.trim();
        task.progress = -1;
        task = AudioProcessorTask.toServer(task);
        this.dao
          .AddTask(task)
          .toPromise()
          .then((data: any) => {
            task = data;
            this.taskModel.statusMessage = "Uploading, please don't close this window...";
            let promises = [];
            for (var i = 0; i < this.taskModel.task.taskBlocks.length; i++) {
              let block = this.taskModel.task.taskBlocks[i];
              promises.push(this.upload(block.tileItem));
            }
            return Promise.all(promises);
          })
          .then((sessionIds: string[]) => {
            return new Promise<string[]>((resolve, reject) => {
              setTimeout(() => {
                resolve(sessionIds);
              }, 1000);
            });
          })
          .then((sessionIds: string[]) => {
            return this.dao.PersistAndStart(sessionIds, task.id).toPromise();
          })
          .then(() => {
            this.taskModel.statusMessage = "";
            this.disableLoading();
            this.taskModel.clearTask();
            this.router.navigate(["processing"]);
          }, () => {
            this.taskModel.statusMessage = "";
            this.disableLoading();
          })
      })
  }

  upload(tileItem: ITileItem) {
    return new Promise<string>((resolve, reject) => {
      this.oidc.getAccessToken().toPromise()
        .then((token: any) => {
          var userId = this.user.id;
          let sessionId: string = "";
          var r = new Resumable({
            target: environment.endpoints['Luigi.Service'] + '/file/upload/' + userId,
            chunkSize: 10 * 1024 * 1024,
            forceChunkSize: true,
            simultaneousUploads: 5,
            headers: {
              Authorization: "Bearer " + token
            },
            uploadMethod: 'PUT',
            testChunks: false,
            throttleProgressCallbacks: 1,
            fileParameterName: 'file',
            chunkNumberParameterName: 'chunkNumber',
            chunkSizeParameterName: 'chunkSize',
            currentChunkSizeParameterName: 'chunkSize',
            fileNameParameterName: 'fileName',
            totalSizeParameterName: 'totalSize'
          });
          r.addFile(tileItem.file)
          r.on('fileAdded', (file: File) => {
            this.dao.CreateUploadSession(file, r.opts.chunkSize, userId)
              .subscribe((result) => {
                let endpoint = environment.endpoints['Luigi.Service'] + '/File/Upload/User/' + userId + '/Session/' + result.sessionId;
                sessionId = result.sessionId;
                r.opts.sessionId = sessionId;
                r.opts.target = endpoint;
                r.upload();
              }, (error) => {

              })
          });
          r.on('pause', () => {

          });
          r.on('complete', () => {
            resolve(sessionId);
          });
          r.on('fileSuccess', (file: any, message: any) => {
          });
          r.on('fileError', (file: any, message: any) => {
            reject();
          });
          r.on('fileProgress', (file: any) => {
            tileItem.progress = file.progress() * 100;
          });
          r.on('cancel', function () {
            reject();
          });
          r.on('uploadStart', function () {
          });
        })
    })
  }

  sanitizeFileName(name: string) {
    return name.replace("[", "_").replace("]", "_");
  }

  formatBytes(block: IAudioProcessorTaskBlock, decimals: number) {
    let item = block.tileItem;
    let bytes = item.file.size;
    if (!bytes)
      return '0 Bytes';

    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  fromSecondsToTimespan(value: any) {
    const sec = parseInt(value, 10);
    let hours = Math.floor(sec / 3600);
    let minutes = Math.floor((sec - (hours * 3600)) / 60);
    let seconds = sec - (hours * 3600) - (minutes * 60);
    let hoursStr = hours.toString();
    let minutesStr = minutes.toString();
    let secondsStr = seconds.toString();
    if (hours < 10) { hoursStr = "0" + hours; }
    if (minutes < 10) { minutesStr = "0" + minutes; }
    if (seconds < 10) { secondsStr = "0" + seconds; }
    return hoursStr + ':' + minutesStr + ':' + secondsStr;
  }

  validate() {
    return new Promise(async (resolve, reject) => {
      const task = this.taskModel.task;
      this.authService
        .sync(true, true)
        .subscribe((user: IUser) => {
          if (task.name.trim().length > MAX_TASK_NAME_LENGTH) {
            this.async.openAlert("Warning", "Task name is too long. Maximum length is " + MAX_TASK_NAME_LENGTH + " characters", () => { });
            reject();
          }
          if (!task.name.trim()) {
            this.async.openAlert("Warning", "Task name is missing, please type a name in the name input box above", () => { });
            reject();
          }
          else if (!task.taskBlocks.length) {
            this.async.openAlert("Warning", "You must add one or more files to process", () => { });
            reject();
          }
          else if (task.computingUnits > (user.computingUnits + user.computingUnitsExtra)) {
            this.async.openAlert("Warning", "You don't have enugh computing units", () => { });
            reject();
          } else {
            resolve(true);
          }
          return of(null);
        })
    })
  }

  enableLoading() {
    this.loader.start();
  }

  disableLoading() {
    this.loader.stop();
  }

  stopAllPlayers(event: Event) {

  }

  openInfoBlock(event: Event) {
    const eventData = (event as any).data;
    this.taskCreatorInfoModalRef = this.dialog.open(TaskCreatorInfoComponent, {
      width: "500px",
      data: {
        block: eventData.block,
        toMenuItems: eventData.toMenuItems
      }
    });
    this.taskCreatorInfoModalRef.afterClosed().subscribe((result) => {
    });
  }
}
